import {
  Api,
  ApiResponse,
  processApiResponse,
  ApiResponseFormat,
  paginatedProcessResponseResolver,
  emptyProcessResponseResolver,
  Sortable,
  sortableToApiFormat,
  PaginationFilters, getConfig,
} from '@moodys/cre-cpm-client-apis.apis.api-base';
import { createConsumer } from "@rails/actioncable"
import { Company, QPSignature, Session, User } from './models/response';
import {
  matchingCompanyTypesFromClientToServer,
  matchingCompanyTypesFromServerToClient,
} from './functions';
import { CompanyFilter, UserFilter } from './models/request';
import {
  companyAttributes,
  companyUpdateAttributes,
} from './models/response/company';
import { userAttributes } from './models/response/user';
import { Comment } from './models/response/comment.model';
import { CommentedEntity } from './models/response/commented-entity.model';
import { Notification, NotificationAttributes } from './models/response/notification';
import {
  CreateNotificationData,
  NotificationFilter,
} from './models/request/notification-filters';

class AuthApi extends Api {
  // ***** Session *****
  public async getSession(): Promise<ApiResponse<Session>> {
    const response = await this.request.get<ApiResponseFormat<Session>>(
      '/v1/auth/sessions',
      {
        access_token: this.token,
        fields: {
          sessions: ['user', 'company', 'feature_access', 'iss', 'sub'].join(
            ','
          ),
        },
      },
      {
        headers: this.getHeaders(),
      }
    );

    return processApiResponse(response);
  }

  public async logout(): Promise<ApiResponse<void>> {
    try {
      await this.request.delete<ApiResponse<Session>>(
        '/v1/auth/sessions',
        {},
        {
          headers: this.getHeaders(),
        }
      );
    } catch {
      // Do nothing
    }
    return Promise.resolve({ kind: 'ok', data: undefined });
  }

  public async postUserSession(
    email: string,
    password: string
  ): Promise<ApiResponse<Session>> {
    const body = {
      data: {
        type: 'sessions',
        relationships: {
          user: {
            type: 'users',
            attributes: {
              email,
              password,
            },
          },
        },
      },
      fields: {
        sessions: 'token',
      },
    };

    const response = await this.request.post<ApiResponseFormat<Session>>(
      '/v1/auth/sessions',
      body
    );

    return processApiResponse(response);
  }

  public async createUserRecover(
    email: string,
    callback: string
  ): Promise<ApiResponse<void>> {
    const body = {
      data: {
        type: 'recovers',
        attributes: {
          callback,
        },
        relationships: {
          user: {
            type: 'users',
            attributes: {
              email,
            },
          },
        },
      },
    };

    const response = await this.request.post<ApiResponseFormat<void>>(
      '/v1/auth/recovers',
      body
    );

    return processApiResponse(response);
  }

  // ***** Users *****
  public async getUsers(
    pagination: PaginationFilters,
    filters?: UserFilter,
    sorting?: Sortable
  ): Promise<ApiResponse<User[]>> {
    const params = {
      fields: {
        users: [
          'name',
          'family_name',
          'email',
          'user_type',
          'last_sign_in_at',
          'portfolios',
          'locked_at',
          'active',
          'expiration_date',
        ].join(','),
        companies: ['id', 'name'].join(','),
      },
      page: pagination,
      filter: {
        like: filters?.like,
        company_ref_uuid: filters?.companyUuid,
        user_type: filters?.userType,
        locked_at: filters?.lockedAtMax,
        expiration_date: filters?.expirationDateMax,
        id: filters?.id,
      },
      sort: sortableToApiFormat(sorting),
    };

    const response = await this.request.get<ApiResponseFormat<User[]>>(
      '/v1/auth/users',
      params,
      {
        headers: this.getHeaders(),
      }
    );

    return processApiResponse<User[]>(
      response,
      paginatedProcessResponseResolver
    );
  }

  public async getUser(userId: string): Promise<ApiResponse<User>> {
    const params = {
      fields: {
        users: userAttributes.join(','),
        companies: ['id'].join(','),
      },
    };

    const response = await this.request.get<ApiResponseFormat<User>>(
      `/v1/auth/users/${userId}`,
      params,
      {
        headers: this.getHeaders(),
      }
    );

    return processApiResponse(response);
  }

  public async updateUserPassword(
    newPassword: string,
    token: string,
    userId: number
  ): Promise<ApiResponse<User>> {
    const body = {
      data: {
        type: 'users',
        id: userId,
        attributes: {
          password: newPassword,
        },
      },
      fields: {
        users: 'email',
      },
    };

    const response = await this.request.put<ApiResponseFormat<User>>(
      '/v1/auth/users',
      body,
      {
        headers: { Authorization: `Bearer ${token}` },
      }
    );

    return processApiResponse(response);
  }

  public async updateUser(user: User): Promise<ApiResponse<User>> {
    const body = {
      fields: {
        users: userAttributes.join(','),
      },
      data: user,
      allowNullCollection: ['locked_at', 'external_organization'],
    };

    const response = await this.request.put<ApiResponseFormat<User>>(
      `/v1/auth/users/${user.id}`,
      body,
      {
        headers: this.getHeaders(),
      }
    );

    return processApiResponse(response);
  }

  public async createUser(user: User): Promise<ApiResponse<User>> {
    const body = {
      fields: {
        users: userAttributes.join(','),
      },
      data: user,
      allowNullCollection: ['locked_at'],
    };

    const response = await this.request.post<ApiResponseFormat<User>>(
      '/v1/auth/users',
      body,
      {
        headers: this.getHeaders(),
      }
    );

    return processApiResponse(response);
  }

  public async resendInvitation(
    userId: string,
    callback: string
  ): Promise<ApiResponse<void>> {
    const body = {
      data: {
        type: 'invitations',
        attributes: {
          callback,
        },
      },
    };
    const response = await this.request.post<ApiResponseFormat<void>>(
      `/v1/auth/users/${userId}/invitations`,
      body,
      {
        headers: this.getHeaders(),
      }
    );

    return processApiResponse(response, emptyProcessResponseResolver);
  }

  public async lockUser(
    userId: number,
    lockAt: number | null,
    companyUuid: string
  ): Promise<ApiResponse<void>> {
    const body = {
      fields: {
        users: 'locked_at',
      },
      data: {
        type: 'users',
        id: userId,
        attributes: {
          locked_at: lockAt,
        },
        relationships: {
          company: {
            type: 'companies',
            id: companyUuid,
            attributes: {
              company_ref_uuid: companyUuid,
            },
          },
        },
      },
      allowNullCollection: ['locked_at'],
    };

    const response = await this.request.put<ApiResponseFormat<void>>(
      `/v1/auth/users/${userId}`,
      body,
      {
        headers: this.getHeaders(),
      }
    );

    return processApiResponse(response, emptyProcessResponseResolver);
  }

  // ***** Company *****
  public async getCompany(companyUuid: string): Promise<ApiResponse<Company>> {
    const params = {
      fields: {
        companies: companyAttributes.join(','),
      },
    };

    const response = await this.request.get<ApiResponseFormat<Company>>(
      `/v1/auth/companies/${companyUuid}`,
      params,
      {
        headers: this.getHeaders(),
      }
    );

    return processApiResponse(response, (data) => ({
      data: matchingCompanyTypesFromServerToClient(data.data),
    }));
  }

  public async getCompanies(
    pagination: PaginationFilters,
    filters?: CompanyFilter,
    sorting?: Sortable
  ): Promise<ApiResponse<Company[]>> {
    const params = {
      fields: {
        companies: [
          'name',
          'language',
          'internal_users_count',
          'external_users_count',
          'internal_users_limit',
          'external_users_limit',
        ].join(','),
      },
      page: pagination,
      filter: {
        like: filters?.like,
      },
      sort: sortableToApiFormat(sorting),
    };

    const response = await this.request.get<ApiResponseFormat<Company[]>>(
      '/v1/auth/companies',
      params,
      {
        headers: this.getHeaders(),
      }
    );

    return processApiResponse(response, (data) => ({
      data: (data.data ?? []).map((company: Company) =>
        matchingCompanyTypesFromServerToClient(company)
      ),
      pagination: response.data!.pagination,
    }));
  }

  public async createCompany(company: Company): Promise<ApiResponse<Company>> {
    const body = {
      data: matchingCompanyTypesFromClientToServer(company),
    };

    const response = await this.request.post<ApiResponseFormat<Company>>(
      '/v1/auth/companies',
      body,
      {
        headers: this.getHeaders(),
      }
    );

    return processApiResponse(response, (data) => ({
      data: matchingCompanyTypesFromServerToClient(data.data),
    }));
  }

  public async updateCompany(company: Company): Promise<ApiResponse<Company>> {
    const body = {
      fields: {
        companies: companyUpdateAttributes.join(','),
      },
      data: matchingCompanyTypesFromClientToServer(company),
    };

    const response = await this.request.put<ApiResponseFormat<Company>>(
      `/v1/auth/companies/${company.id}`,
      body,
      {
        headers: this.getHeaders(),
      }
    );

    return processApiResponse(response, (data) => ({
      data: matchingCompanyTypesFromServerToClient(data.data),
    }));
  }

  public async getEmbededQuiksightUrl(
    userId: string
  ): Promise<ApiResponse<User>> {
    const params = {
      fields: {
        users: ['quicksight_embed_url'].join(','),
      },
    };

    const response = await this.request.get<ApiResponseFormat<User>>(
      `/v1/auth/users/${userId}`,
      params,
      {
        headers: this.getHeaders(),
      }
    );
    return processApiResponse(response);
  }

  public async getQPSignature(
    widgetPath: string
  ): Promise<ApiResponse<QPSignature>> {
    const body = {
      data: {
        type: 'signatures',
        attributes: {
          widget_path: widgetPath,
        },
      },
      fields: {
        signatures: 'signature,request_date',
      },
    };
    const response = await this.request.post<ApiResponseFormat<QPSignature>>(
      '/v1/auth/quiqproperty/signatures',
      body,
      {
        headers: this.getHeaders(),
      }
    );
    return processApiResponse(response);
  }

  public async getComments(
    entityId: string,
    entityType: string
  ): Promise<ApiResponse<Comment[]>> {
    const params = {
      fields: {
        comments: ['id', 'text', 'created_at'].join(','),
        commented_entity: ['id'].join(','),
        users_related: ['id'].join(','),
        user_creator: ['id'].join(','),
      },
      filter: {
        commented_entity: {
          id: entityId,
          type: entityType,
        },
      },
    };

    const response = await this.request.get<ApiResponseFormat<Comment[]>>(
      `v1/auth/comments`,
      params,
      {
        headers: this.getHeaders(),
      }
    );

    return processApiResponse<Comment[]>(response);
  }

  public async getComment(commentId: string): Promise<ApiResponse<Comment>> {
    const params = {
      fields: {
        comments: ['id', 'text', 'created_at'].join(','),
        commented_entity: ['id'].join(','),
        users_related: ['id'].join(','),
        user_creator: ['id'].join(','),
      },
    };

    const response = await this.request.get<ApiResponseFormat<Comment>>(
      `v1/auth/comments/${commentId}`,
      params,
      {
        headers: this.getHeaders(),
      }
    );

    return processApiResponse<Comment>(response);
  }

  public async updateComment(
    commentId: string,
    text: string
  ): Promise<ApiResponse<CommentedEntity>> {
    const body = {
      data: {
        type: 'comments',
        attributes: { text },
      },
    };

    const response = await this.request.put<ApiResponseFormat<CommentedEntity>>(
      `v1/auth/comments/${commentId}`,
      body,
      {
        headers: this.getHeaders(),
      }
    );

    return processApiResponse<CommentedEntity>(response);
  }

  public async createComment(
    entityId: string,
    entityType: string,
    text: string,
    users_related: { id: string; type: string }[]
  ): Promise<ApiResponse<Comment>> {
    const body = {
      data: {
        type: 'comments',
        attributes: {
          text,
        },
        relationships: {
          commented_entity: {
            id: entityId,
            type: entityType,
          },
          users_related,
        },
      },
    };

    const response = await this.request.post<ApiResponseFormat<Comment>>(
      `/v1/auth/comments`,
      body,
      {
        headers: this.getHeaders(),
      }
    );

    return processApiResponse<Comment>(response);
  }

  public async deleteComment(commentId: string): Promise<ApiResponse<void>> {
    const response = await this.request.delete<ApiResponseFormat<void>>(
      `/v1/auth/comments/${commentId}`,
      '',
      {
        headers: this.getHeaders(),
      }
    );

    return processApiResponse<void>(response, emptyProcessResponseResolver);
  }

  public async getCommentedEntities(
    entityId: string,
    entityType: string
  ): Promise<ApiResponse<CommentedEntity[]>> {
    const params = {
      fields: {
        user_commented_entities: ['updated_at'].join(','),
        commented_entity: ['id'].join(','),
      },
      filter: {
        commented_entity: {
          id: entityId,
          type: entityType,
        },
      },
    };

    const response = await this.request.get<
      ApiResponseFormat<CommentedEntity[]>
    >('/v1/auth/users/commented_entities', params, {
      headers: this.getHeaders(),
    });

    return processApiResponse<CommentedEntity[]>(response);
  }

  public async updateCommentedEntity(
    entityId: string,
    entityType: string
  ): Promise<ApiResponse<CommentedEntity>> {
    const body = {
      data: {
        type: 'commented_entities',
        relationships: {
          commented_entity: {
            id: entityId,
            type: entityType,
          },
        },
      },
    };

    const response = await this.request.put<ApiResponseFormat<CommentedEntity>>(
      '/v1/auth/users/commented_entities',
      body,
      {
        headers: this.getHeaders(),
      }
    );

    return processApiResponse<CommentedEntity>(response);
  }

  // ***** Notification *****
  private notificationFields = {
    notifications: [
      'payload',
      'notification_type',
      'priority',
      'template_id',
      'read',
      'created_at',
      'notification_id'
    ].join(','),
  };

  public async getNotifications(
    userId: string,
    pagination: PaginationFilters,
    filters?: NotificationFilter,
    sorting?: Sortable
  ): Promise<ApiResponse<Notification[]>> {
    const params = {
      fields: this.notificationFields,
      page: pagination,
      filter: {
        notification_type: filters?.notificationType,
        priority: filters?.priority,
        category: filters?.category,
        read: filters?.read,
      },
      sort: sortableToApiFormat(
        sorting || { fieldName: 'created_at', fieldOrder: 'descend' }
      ),
    };

    const response = await this.request.get<
      ApiResponseFormat<Notification[]>
    >(`/v1/auth/users/${userId}/notifications`, params, {
      headers: this.getHeaders(),
    });

    return processApiResponse<Notification[]>(
      response,
      paginatedProcessResponseResolver
    );
  }

  public async getNotification(
    userId: string,
    notificationId: string
  ): Promise<ApiResponse<Notification>> {
    const params = {
      fields: this.notificationFields,
    };

    const response = await this.request.get<
      ApiResponseFormat<Notification>
    >(`/v1/auth/users/${userId}/notifications/${notificationId}`, params, {
      headers: this.getHeaders(),
    });

    return processApiResponse<Notification>(
      response,
      paginatedProcessResponseResolver
    );
  }

  public async updateNotification(
    userId: string,
    notificationId: string
  ): Promise<ApiResponse<Notification>> {
    const params = {
      fields: this.notificationFields,
      data: {
        type: 'notifications',
        attributes: {
          read: true,
        },
      },
    };

    const response = await this.request.put<
      ApiResponseFormat<Notification>
    >(`/v1/auth/users/${userId}/notifications/${notificationId}`, params, {
      headers: this.getHeaders(),
    });

    return processApiResponse<Notification>(response);
  }

  public async createNotification(
    userId: string,
    data: CreateNotificationData
  ): Promise<ApiResponse<Notification>> {
    const params = {
      fields: this.notificationFields,
      data: {
        type: 'notifications',
        attributes: {
          template_id: data.template_id,
          notification_type: data.notification_type,
          priority: data.priority,
        },
      },
    };

    const response = await this.request.post<
      ApiResponseFormat<Notification>
    >(`/v1/auth/users/${userId}/notifications`, params, {
      headers: this.getHeaders(),
    });

    return processApiResponse<Notification>(response);
  }

  // eslint-disable-next-line class-methods-use-this
  public connectToNotificationService(
    channel: string,
    userId: string,
    onReceived: (data: NotificationAttributes) => void
  ): void {
    if (this.hasToken()) {
      const cable = createConsumer(
        `${process.env.REACT_APP_AUTH_API_BASE?.replace(
          /^http/,
          'ws'
        )}/cable?access_token=${this.token}`
      );

      cable.subscriptions.create({ channel, user_id: userId }, { received: onReceived })
    }
  }

  public getCredaUrl(assetId: string): string {
    return `${process.env.REACT_APP_API_BASE}/v0/exports/creda/${assetId}?access_token=${this.token}`;
  }
}

export const authApi = new AuthApi();
export const authApiWithEnvURLConfig = new AuthApi(getConfig({url: process.env.REACT_APP_AUTH_API_BASE}));

export { AuthApi };
